import { useMemo } from "react";

import { formatDate, formatDateContext, formatNumberFinancial, formatPrice } from "utility/formatters";

import { combinePositionLots } from "./combinePositionLots";
import { sumRowsGainLoss } from "./futuresOptionsView";

// Constants
const BUY_SELL_CODE_BUY = "1";
const SECURITY_SUBTYPE_CALL = "C";
const SECURITY_SUBTYPE_PUT = "P";

const getSecurityType = (securitySubtypeCode) => {
  if (securitySubtypeCode === SECURITY_SUBTYPE_CALL) return "Call";
  if (securitySubtypeCode === SECURITY_SUBTYPE_PUT) return "Put";
  return "";
};

const transformRow = (row) => {
  if (!row) return null;
  const security = getSecurityType(row.security_subtype_code);
  return {
    ...row,
    position: `${String(row.buy_sell_code) === BUY_SELL_CODE_BUY ? "Buy" : "Sell"} ${security}`,
    trade_date_formatted: formatDate(row.trade_date),
    orig_qty_formatted: formatNumberFinancial(row.quantity * row.multiplication_factor, 0),
    accumulated_formatted: row.quantity_accumulated
      ? formatNumberFinancial(row.quantity_accumulated * row.multiplication_factor, 0)
      : "",
    open_qty_formatted: formatNumberFinancial(row.quantity_open * row.multiplication_factor, 0),
    potential_remaining_formatted: row.quantity_potential_remaining
      ? formatNumberFinancial(row.quantity_potential_remaining * row.multiplication_factor, 0)
      : "",
    contract_formatted: `${row.security_desc_line_1?.toLowerCase()}${
      row.option_expire_date ? `\nExp ${formatDate(row.option_expire_date)}` : ""
    }${row.option_strike_price ? `\n${formatPrice(row.option_strike_price, 4)}` : ""}`,
    price_formatted: formatPrice(row.trade_price, 4),
    current_price_formatted: row.quote_open ? formatPrice(row.quote_open, 4) : null,
    current_price_context: formatDateContext(row?.market_value_timestamp),
    gain_loss_formatted: row.market_value_open ? formatPrice(row.market_value_open, 2) : null,
    closed_gain_loss_formatted: row.market_value_closed ? formatPrice(row.market_value_closed, 2) : null,
    is_open_formatted: row.is_open ? "Open" : "Closed",
    option_expire_date_formatted: row.option_expire_date ? formatDate(row.option_expire_date) : "",
  };
};

/** Pure build: filter already applied to mainRows. */
export function buildFuturesOptionsView(mainRows, getTransactionHistoryRowsByMatch) {
  const rows = mainRows || [];
  const groups = combinePositionLots(rows);

  const data = groups.map(
    ({
      head,
      lots,
      is_combined,
      quantity,
      quantity_open,
      quantity_accumulated,
      quantity_potential_remaining,
      trade_price,
    }) => {
      const transformedLots = lots.map((lot) => ({
        ...transformRow(lot),
        offsets: (getTransactionHistoryRowsByMatch(lot.id) || []).map(transformRow),
      }));

      const transformedMainRow = transformRow({
        ...head,
        quantity,
        quantity_open,
        quantity_accumulated,
        quantity_potential_remaining,
        trade_price,
      });

      const headOffsets = transformedLots[0]?.offsets || [];
      const has_offsets = transformedLots.some((lot) => lot.offsets?.length > 0);
      const is_accordion = is_combined || has_offsets;

        return {
          ...transformedMainRow,
          is_combined,
          is_accordion,
          lots: is_combined ? transformedLots : [],
          offsets: !is_combined && has_offsets ? headOffsets : [],
          // Combined parent: no tracer; date blank (lots expand below).
          tracer_number: is_combined ? "" : transformedMainRow.tracer_number,
          trade_date_formatted: is_combined ? "" : transformedMainRow.trade_date_formatted,
          subdata: is_combined ? transformedLots.flatMap((lot) => [lot, ...(lot.offsets || [])]) : headOffsets,
        };
    }
  );

  const totalOrigQty = rows.reduce((total, row) => total + row.quantity * row.multiplication_factor, 0);
  const totalOpenQty = rows.reduce((total, row) => total + row.quantity_open * row.multiplication_factor, 0);
  const totalGainLoss = sumRowsGainLoss(rows, getTransactionHistoryRowsByMatch);

  return {
    data,
    totals: {
      total_orig_qty: totalOrigQty,
      total_open_qty: totalOpenQty,
      total_orig_qty_formatted: formatNumberFinancial(totalOrigQty, 0),
      total_open_qty_formatted: formatNumberFinancial(totalOpenQty, 0),
      total_gain_loss_formatted: formatPrice(totalGainLoss, 2),
      total_gain_loss: totalGainLoss,
    },
  };
}

export function useFuturesOptionsAdapter(
  getTransactionHistoryRowsByCommodity,
  getTransactionHistoryRowsByMatch,
  commodityName,
  rowFilter
) {
  return useMemo(() => {
    if (!commodityName) return buildFuturesOptionsView([], getTransactionHistoryRowsByMatch);
    const mainRows = (getTransactionHistoryRowsByCommodity(commodityName) || []).filter(
      (row) => !rowFilter || rowFilter(row)
    );
    return buildFuturesOptionsView(mainRows, getTransactionHistoryRowsByMatch);
  }, [getTransactionHistoryRowsByCommodity, getTransactionHistoryRowsByMatch, commodityName, rowFilter]);
}
